/**
 * The player, wherever a player appears. The deck reduces this to two shapes:
 * a round avatar and a name in a ranking row, or a cream card with the face
 * above the name on the photo screen. Everything the old card carried in
 * between — the ruled edge, the "THE ID GAME" strapline, the badge line under
 * every name — is narration, and the deck's brief is to remove narration.
 *
 * `chip`  — the 32px-avatar row. Ranking, leaderboards.
 * `card`  — face over name, on cream. The photo preview, and the prediction grid.
 *
 * DESIGN.md §3.4 drew prediction as a 2-column grid of avatar+name rows — the
 * chip shape — and the card shape was described here as the photo screen's
 * alone. That is reversed. The game is played with faces on cards; a list of
 * name rows is a contact list, and a predictor scanning eleven people for the
 * one they think goes first is looking for a face, not reading a column of
 * names. So `card` now has two sizes: `full` (the photo screen's one big card)
 * and `tile` (a dozen of them in a grid that must not scroll), and it can carry
 * a corner `badge` — the `1ST` / `LAST` marks the prediction grid stamps on the
 * two faces that are spoken for.
 */
import type { ReactNode } from 'react';
import type { PlayerId } from '@idgame/protocol';

export type LicenseVariant = 'chip' | 'card';

export interface LicensePlayer {
  id: PlayerId;
  name: string;
  photo: string | null;
  isSelf?: boolean;
  connected?: boolean;
}

export interface LicenseCardProps {
  player: LicensePlayer;
  variant?: LicenseVariant;
  /** Rank number at the left of a chip, e.g. 3. The card shape never has one. */
  rank?: number | null;
  /** The two slots with money on them (SPEC §2.3 phase 4) are filled, not ringed. */
  tone?: 'neutral' | 'top' | 'bottom';
  selected?: boolean;
  dimmed?: boolean;
  /**
   * `card` only. `full` is the deck's photo card — most of a phone wide, face
   * sized by the card's own width. `tile` is one of a dozen in a grid whose
   * height is fixed by the screen, so its face is sized to fit the cell and its
   * name drops to 12px: at four columns on a 390px phone a 16px name is two
   * truncated syllables.
   */
  size?: 'full' | 'tile';
  /**
   * `card` only. A small cream mark in the top-right corner of the face —
   * `1ST`, `LAST`, a tick. It sits over the photo rather than under the name
   * because the name is the one line the card cannot afford to lose.
   */
  badge?: ReactNode;
  className?: string;
  /** Extra content at the end of the row — attribution chips, score deltas. */
  children?: ReactNode;
}

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?';
}

/**
 * A player's face, wherever one appears. The deck draws it as a circle in a
 * ranking row and as a rounded square on a prediction tile or photo card, but
 * the fallback is the same either way: an initial on `cream-deep`, because a
 * player without a photo still has to be findable at a glance in a list of
 * twelve. The face is always decorative — the name beside it carries the
 * meaning — so it is `aria-hidden` in both shapes.
 */
export function PlayerAvatar({
  player,
  size,
  shape = 'round',
  dimmed = false,
}: {
  player: LicensePlayer;
  /** Tailwind height/width pair, e.g. `h-8 w-8`. */
  size: string;
  shape?: 'round' | 'square';
  dimmed?: boolean;
}) {
  const box = [
    size,
    shape === 'round' ? 'rounded-full' : 'rounded-[0.5rem]',
    'shrink-0 object-cover',
    dimmed ? 'opacity-45' : '',
  ].join(' ');

  if (player.photo) {
    return <img src={player.photo} alt="" aria-hidden="true" className={box} draggable={false} />;
  }
  return (
    <span
      aria-hidden="true"
      className={`${box} flex items-center justify-center bg-cream-deep font-semibold text-ink-soft`}
    >
      {initials(player.name)}
    </span>
  );
}

export function LicenseCard({
  player,
  variant = 'chip',
  rank = null,
  tone = 'neutral',
  selected = false,
  dimmed = false,
  size = 'full',
  badge,
  className = '',
  children,
}: LicenseCardProps) {
  // Filled terracotta is the deck's one emphasis: live selection, and the two
  // ends of a revealed ranking. Everything else is cream on a hairline.
  const filled = selected || tone !== 'neutral';
  const surface = dimmed
    ? 'border-transparent bg-disabled text-ink-soft/45'
    : filled
      ? 'border-terracotta bg-terracotta text-cream'
      : 'border-line bg-cream text-ink';
  // A tile is always 2px, picked or not: a dozen of them sit under a finger
  // that is changing its mind, and a border that thickens on selection moves
  // every face in the grid by a pixel each time.
  const tile = variant === 'card' && size === 'tile';
  const base = `rounded-card border ${selected || tile ? 'border-2' : ''} ${surface}`;

  const disconnected = player.connected === false;

  if (variant === 'chip') {
    return (
      <div
        data-testid={`license-${player.id}`}
        data-variant="chip"
        className={`${base} flex min-h-11 items-center gap-2.5 px-3 py-1.5 ${className}`}
      >
        {rank !== null && (
          <span
            className={`w-5 shrink-0 text-[0.8125rem] tabular-nums ${
              filled && !dimmed ? 'text-cream' : 'text-ink-soft'
            }`}
          >
            {rank}
          </span>
        )}
        <PlayerAvatar player={player} size="h-8 w-8" />
        <span className="min-w-0 flex-1 truncate text-[0.875rem] font-semibold">
          {player.name}
          {/* Set in caps by CSS, not by the string: the row still reads "You"
              to a screen reader, and to anyone matching on the text. */}
          {player.isSelf ? <span className="font-normal uppercase"> · You</span> : null}
          {disconnected ? (
            <span className="font-normal opacity-70"> · offline</span>
          ) : null}
        </span>
        {children}
      </div>
    );
  }

  return (
    <div
      data-testid={`license-${player.id}`}
      data-variant={variant}
      data-size={size}
      className={`${base} relative flex flex-col items-center ${
        tile ? 'h-full min-h-0 gap-1 p-1.5' : 'gap-2 p-3'
      } ${className}`}
    >
      {/* Fills the card rather than sitting at a fixed 128px, so the caller
          sizes the face by sizing the card — the deck's photo card is most of
          a phone width, the one on the waiting screen is a third of it.

          A tile fills its cell in BOTH axes, and does not hold a square. It
          used to be `aspect-square`, which looked correct in isolation and wrong
          on the phone: the grid divides the screen's leftover height into rows,
          so a cell at twelve players is about twice as tall as it is wide, and a
          square face left a band of empty cream above and below every person.
          Twelve cards each two-thirds empty is not the license deck this game is
          named for — it reads as a grid of placeholders. Letting the face take
          the whole cell and crop (`object-cover`, already on the box) makes it a
          portrait, which is what a license card is, and makes the faces larger
          at exactly the player count where they are hardest to tell apart. */}
      <span
        className={
          tile
            ? 'flex min-h-0 w-full flex-1 items-stretch justify-center overflow-hidden'
            : 'contents'
        }
      >
        <PlayerAvatar
          player={player}
          size={tile ? 'h-full max-h-full w-full min-h-0' : 'aspect-square w-full'}
          shape="square"
          dimmed={dimmed}
        />
      </span>
      <div className="w-full text-center">
        <div
          className={`truncate font-semibold tracking-[0.06em] uppercase ${
            tile ? 'text-[0.75rem] leading-tight' : 'text-base'
          }`}
        >
          {player.name}
        </div>
        {disconnected && (
          <div className={`opacity-70 ${tile ? 'text-[0.5625rem]' : 'text-[0.6875rem]'}`}>
            Offline
          </div>
        )}
      </div>
      {badge && (
        // Cream on the terracotta fill, so the mark reads as stamped on the
        // card rather than as another tile stacked on top of it.
        <span
          data-testid={`license-badge-${player.id}`}
          className="absolute top-2 right-2 rounded-[0.375rem] bg-cream px-1.5 py-0.5 text-[0.5625rem] font-semibold tracking-[0.1em] text-terracotta uppercase"
        >
          {badge}
        </span>
      )}
      {children}
    </div>
  );
}
